import { useRouter } from 'next/router';
import { ComponentProps, FC, useContext } from 'react';

import { useTranslation } from 'utils/i18n';
import useRelatedSpaces from '../../hooks/useRelatedSpaces';
import { SearchContext } from '../../state/search';
import FavouriteSpaceBase from '../favouriteSpace';

const FavouriteSpace = FavouriteSpaceBase as FC<
  Omit<ComponentProps<typeof FavouriteSpaceBase>, 'navigateToSpace'> & {
    navigateToSpace?: boolean;
  }
>;

interface Space {
  id: number;
  venue: Venue;
  categories: string[];
}

interface Venue {
  id: number;
  name: string;
  address: Address;
}

interface Address {
  postcode: string;
}

interface Props {
  space: Space;
  disabledSpaceIds: number[];
  selectSpace?: (space: Space, venue: Venue) => void;
}

const RelatedSpaces: FC<Props> = ({
  space,
  disabledSpaceIds = [],
  selectSpace,
}) => {
  const { t } = useTranslation('common');
  const { data: relatedSpaces } = useRelatedSpaces(space.id);
  const router = useRouter();

  const { actions } = useContext(SearchContext);

  const viewAll = () => {
    actions.updateLocation(space.venue.address.postcode);
    actions.updateDistance(5);
    actions.updateRoomTypes([space.categories[0]]);

    router.push(`/find-rooms`);
  };

  if (relatedSpaces.length === 0) {
    return null;
  }

  return (
    <div className="related-spaces">
      <div className="related-spaces__header">
        <h3 className="related-spaces__title">{t('related_spaces.title')}</h3>

        <button onClick={viewAll} className="button button--text">
          {t('related_spaces.view_all')}
        </button>
      </div>

      <div className="related-spaces__inner">
        {relatedSpaces.map(relatedSpace => (
          <FavouriteSpace
            key={`space-${relatedSpace.id}`}
            space={relatedSpace}
            selectSpace={
              selectSpace && !disabledSpaceIds.includes(Number(relatedSpace.id))
                ? () =>
                    selectSpace(
                      relatedSpace as unknown as Space,
                      relatedSpace.venue as unknown as Venue
                    )
                : undefined
            }
          />
        ))}
      </div>
    </div>
  );
};

export default RelatedSpaces;
